import {
  type Entitlement,
  EntitlementName,
} from "@shared/types/localTypes/entitlements";
import {
  AddonDTO,
  type EntitlementDTO,
  type SubscriptionDTO,
} from "@shared/types/common";
import { DateTime } from "luxon";
import { subscriptionsService } from "@shared/services/subscriptions";
import { entitlementsConfig } from "@shared/statics/entitlements";
import { ResponseType as PartnerAddonDTO } from "@shared/services/partners/partnerSubscription/response.ts";
import { usePartnerTableStore } from "@shared/stores/partner/partnerTable.ts";

export const entitlementsService = {
  getEntitlement: (
    subscription: SubscriptionDTO | null,
    expectedEntitlement: EntitlementName,
  ): Entitlement | undefined => {
    if (!subscription) {
      return;
    }

    const directEntitlement = findEntitlement(
      subscription.entitlements,
      expectedEntitlement,
    );
    if (directEntitlement) {
      const isTrialSaas =
        subscription.bom?.trial && subscriptionsService.isSaas(subscription);

      return {
        ...directEntitlement,
        trial: subscription.bom?.trial,
        startDate: Number(subscription.startDate),
        endDate: Number(
          isTrialSaas ? subscription.endOfTrial : subscription.expirationDate,
        ),
      };
    }

    for (const addon of subscription.addons) {
      const addonEntitlement = findEntitlement(
        addon.entitlements,
        expectedEntitlement,
      );
      if (addonEntitlement) {
        return {
          ...addonEntitlement,
          trial: addon.bom.trial,
          startDate: Number(addon.startDate),
          endDate: Number(addon.expirationDate),
        };
      }
    }
  },
  getAddonsEntitlement(
    expectedEntitlement: EntitlementName,
    addons?: AddonDTO[] | PartnerAddonDTO["addons"],
  ) {
    if (!addons) {
      return null;
    }

    for (const addon of addons) {
      const addonEntitlement = findEntitlement(
        addon.entitlements,
        expectedEntitlement,
      );
      if (addonEntitlement) {
        return {
          ...addonEntitlement,
          trial: addon.bom.trial,
          startDate: Number(addon.startDate),
          endDate: Number(addon.expirationDate),
        };
      }
    }
  },
  isExpired: (entitlement?: Entitlement) =>
    entitlement &&
    DateTime.fromMillis(entitlement.endDate).startOf("day") <
      DateTime.now().startOf("day"),

  getEntitlementConfig: (entitlementName: string) => {
    return entitlementsConfig.find(
      (config) =>
        config.entitlementName.toLowerCase() === entitlementName.toLowerCase(),
    );
  },
  hasPartnerEntitlement: (entitlementName: EntitlementName) => {
    const partnerTableStore = usePartnerTableStore();

    const { entitlements, addons } =
      partnerTableStore.subscriptionDetails || {};

    const entitlementConfig =
      entitlementsService.getEntitlementConfig(entitlementName);

    if (!entitlementConfig) {
      return false;
    }

    return (
      entitlements?.some((e) => e.name.toUpperCase() === entitlementName) ||
      entitlementsService.getAddonsEntitlement(entitlementName, addons)
    );
  },
};

const findEntitlement = (
  entitlements: EntitlementDTO[],
  expectedEntitlement: EntitlementName,
) =>
  entitlements
    .filter((entitlement) => !entitlement.excluded)
    .find(
      (entitlement) => entitlement.name.toUpperCase() === expectedEntitlement,
    );
